"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { useWallet } from "../contexts/WalletContext"
import { WalletIcon, Plus, Minus, History, Smartphone, Phone, CreditCard, X } from "lucide-react"
import Card from "../components/Card"
import Button from "../components/Button"
import toast from "react-hot-toast"

const Wallet = () => {
  const { user } = useAuth()
  const { transactions, addTransaction, addBalance } = useWallet()
  const navigate = useNavigate()

  const [showFundModal, setShowFundModal] = useState(false)
  const [fundAmount, setFundAmount] = useState("")
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  const quickAmounts = [1000, 2000, 5000, 10000, 20000, 50000]

  useEffect(() => {
    if (!user) {
      navigate("/login")
    }
  }, [user, navigate])

  const validateFundAmount = () => {
    const newErrors = {}

    if (!fundAmount) {
      newErrors.amount = "Amount is required"
    } else if (isNaN(fundAmount) || Number.parseFloat(fundAmount) < 100) {
      newErrors.amount = "Minimum funding amount is ₦100"
    } else if (Number.parseFloat(fundAmount) > 500000) {
      newErrors.amount = "Maximum funding amount is ₦500,000"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleFundWallet = async () => {
    if (!validateFundAmount()) return

    setLoading(true)

    // Simulate payment processing
    try {
      await new Promise((resolve) => setTimeout(resolve, 2000))

      const amount = Number.parseFloat(fundAmount)

      // Add balance
      addBalance(amount)

      // Add transaction
      addTransaction({
        type: "Wallet Funding",
        amount: amount,
        status: "completed",
        transactionId: `FUND${Date.now()}`,
        paymentMethod: "Bank Transfer",
      })

      toast.success("Wallet funded successfully!")
      setShowFundModal(false)
      setFundAmount("")

      // Update user balance in localStorage
      const updatedUser = { ...user, balance: user.balance + amount }
      localStorage.setItem("user", JSON.stringify(updatedUser))
    } catch (error) {
      toast.error("Funding failed. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const walletTransactions = transactions.filter(
    (t) => t.type.toLowerCase().includes("funding") || t.type.toLowerCase().includes("withdrawal"),
  )

  const totalSpent = transactions
    .filter((t) => !t.type.toLowerCase().includes("funding"))
    .reduce((sum, t) => sum + (t.amount || 0), 0)

  const totalFunded = transactions
    .filter((t) => t.type.toLowerCase().includes("funding"))
    .reduce((sum, t) => sum + (t.amount || 0), 0)

  if (!user) return null

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <WalletIcon className="mr-3 text-green-600" />
            My Wallet
          </h1>
          <p className="text-gray-600 mt-2">Manage your wallet balance and funding</p>
        </div>

        {/* Wallet Balance Card */}
        <Card className="mb-8 bg-gradient-to-r from-green-500 to-blue-600 text-white">
          <div className="text-center py-8">
            <WalletIcon className="h-16 w-16 mx-auto mb-4 opacity-80" />
            <h2 className="text-2xl font-semibold mb-2">Current Balance</h2>
            <p className="text-5xl font-bold mb-6">₦{user.balance?.toLocaleString() || "0"}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => setShowFundModal(true)} className="bg-white text-green-600 hover:bg-gray-100">
                <Plus className="mr-2 h-4 w-4" />
                Fund Wallet
              </Button>
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-green-600"
                onClick={() => navigate("/transactions")}
              >
                <History className="mr-2 h-4 w-4" />
                View History
              </Button>
            </div>
          </div>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Funded</p>
                <p className="text-2xl font-bold text-green-600">₦{totalFunded.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Plus className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Spent</p>
                <p className="text-2xl font-bold text-red-600">₦{totalSpent.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <Minus className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Transactions</p>
                <p className="text-2xl font-bold text-blue-600">{transactions.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <History className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <Card>
            <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Button onClick={() => setShowFundModal(true)} className="w-full justify-start" variant="outline">
                <Plus className="mr-3 h-4 w-4" />
                Fund Wallet
              </Button>
              <Button onClick={() => navigate("/buy-data")} className="w-full justify-start" variant="outline">
                <Smartphone className="mr-3 h-4 w-4" />
                Buy Data
              </Button>
              <Button onClick={() => navigate("/buy-airtime")} className="w-full justify-start" variant="outline">
                <Phone className="mr-3 h-4 w-4" />
                Buy Airtime
              </Button>
              <Button onClick={() => navigate("/pay-bills")} className="w-full justify-start" variant="outline">
                <CreditCard className="mr-3 h-4 w-4" />
                Pay Bills
              </Button>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold mb-4">Funding Options</h3>
            <div className="space-y-3">
              <div className="p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-bold text-sm">₦</span>
                  </div>
                  <div>
                    <p className="font-medium">Bank Transfer</p>
                    <p className="text-sm text-gray-600">Instant funding via bank transfer</p>
                  </div>
                </div>
              </div>
              <div className="p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold text-sm">💳</span>
                  </div>
                  <div>
                    <p className="font-medium">Debit Card</p>
                    <p className="text-sm text-gray-600">Pay with your debit card</p>
                  </div>
                </div>
              </div>
              <div className="p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-bold text-sm">📱</span>
                  </div>
                  <div>
                    <p className="font-medium">USSD</p>
                    <p className="text-sm text-gray-600">Fund via USSD code</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Recent Wallet Transactions */}
        <Card>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold">Recent Wallet Activity</h3>
            <Button variant="outline" size="sm" onClick={() => navigate("/transactions")}>
              View All
            </Button>
          </div>

          {walletTransactions.length > 0 ? (
            <div className="space-y-4">
              {walletTransactions.slice(0, 5).map((transaction) => (
                <div key={transaction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`p-2 rounded-full ${
                        transaction.type.toLowerCase().includes("funding") ? "bg-green-100" : "bg-red-100"
                      }`}
                    >
                      {transaction.type.toLowerCase().includes("funding") ? (
                        <Plus className="h-4 w-4 text-green-600" />
                      ) : (
                        <Minus className="h-4 w-4 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{transaction.type}</p>
                      <p className="text-sm text-gray-600">{new Date(transaction.date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p
                      className={`font-semibold ${
                        transaction.type.toLowerCase().includes("funding") ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {transaction.type.toLowerCase().includes("funding") ? "+" : "-"}₦
                      {transaction.amount?.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">{transaction.status}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <WalletIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No wallet transactions yet</p>
              <p className="text-sm text-gray-500">Fund your wallet to get started</p>
            </div>
          )}
        </Card>

        {/* Fund Wallet Modal */}
        {showFundModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-lg font-semibold">Fund Wallet</h3>
                <button
                  onClick={() => {
                    setShowFundModal(false)
                    setFundAmount("")
                    setErrors({})
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount (₦)</label>
                  <input
                    type="number"
                    value={fundAmount}
                    onChange={(e) => {
                      setFundAmount(e.target.value)
                      if (errors.amount) {
                        setErrors({})
                      }
                    }}
                    placeholder="Enter amount"
                    min="100"
                    max="500000"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.amount ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount}</p>}
                </div>

                {/* Quick Amount Buttons */}
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-2">Quick amounts:</p>
                  <div className="grid grid-cols-3 gap-2">
                    {quickAmounts.map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setFundAmount(amount.toString())}
                        className={`p-2 text-sm border rounded-md hover:bg-gray-50 ${
                          fundAmount === amount.toString()
                            ? "bg-blue-50 border-blue-500 text-blue-600"
                            : "border-gray-300"
                        }`}
                      >
                        ₦{amount.toLocaleString()}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowFundModal(false)
                      setFundAmount("")
                      setErrors({})
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleFundWallet} loading={loading} className="flex-1">
                    Fund Wallet
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Wallet
